class ReusableComponents {
  init() {
    this.#initAutoScroll();
    this.#initGallerySliders();
  }

  #initGallerySliders() {
    for (const slider of document.querySelectorAll('.gallery-slider')) {
      const track = slider.querySelector('.gallery-slider__track');
      if (!track) continue;

      const slides = [...track.querySelectorAll('.gallery-slider__slide')];
      if (!slides.length) continue;

      const prev = slider.querySelector('.gallery-slider__button.prev');
      const next = slider.querySelector('.gallery-slider__button.next');
      const dotsWrap = slider.querySelector('.gallery-slider__dots');
      const loop = slider.dataset.loop === 'true';
      const autoplay = slider.dataset.autoplay === 'true';
      const interval = parseInt(slider.dataset.autoplayInterval, 10) || 4000;

      // Step = one slide + gap.
      const step = () => {
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        return slides[0].offsetWidth + gap;
      };

      // Page size = floor of the configured columns for the current breakpoint,
      // so 1.7 cols scrolls 1 slide, 2.x scrolls 2, etc. (peek never bumps it up).
      const colsMb = parseFloat(slider.dataset.colsMb) || 1;
      const colsTb = parseFloat(slider.dataset.colsTb) || colsMb;
      const colsDk = parseFloat(slider.dataset.cols) || colsTb;
      const cols = () => {
        const w = window.innerWidth;
        const c = w >= 1024 ? colsDk : w >= 540 ? colsTb : colsMb;
        return Math.max(1, Math.floor(c));
      };
      const pages = () => Math.max(1, Math.ceil(slides.length / cols()));
      const atStart = () => track.scrollLeft <= 1;
      const atEnd = () => track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
      const overflows = () => track.scrollWidth - track.clientWidth > 1;

      const scrollByCols = (dir) => {
        track.scrollBy({ left: dir * step() * cols(), behavior: 'smooth' });
      };

      const go = (dir) => {
        if (dir > 0 && atEnd()) {
          if (loop) track.scrollTo({ left: 0, behavior: 'smooth' });
          return;
        }
        if (dir < 0 && atStart()) {
          if (loop) track.scrollTo({ left: track.scrollWidth, behavior: 'smooth' });
          return;
        }
        scrollByCols(dir);
      };

      prev?.addEventListener('click', () => go(-1));
      next?.addEventListener('click', () => go(1));

      let dots = [];

      const buildDots = () => {
        if (!dotsWrap) return;
        const count = pages();
        if (dots.length === count) return;
        dotsWrap.innerHTML = '';
        dots = Array.from({ length: count }, (_, i) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'gallery-slider__dot';
          dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
          dot.addEventListener('click', () => {
            track.scrollTo({ left: i * step() * cols(), behavior: 'smooth' });
          });
          dotsWrap.appendChild(dot);
          return dot;
        });
      };

      const sync = () => {
        if (prev && next) {
          const show = overflows() && !loop;
          prev.classList.toggle('is-disabled', show && atStart());
          next.classList.toggle('is-disabled', show && atEnd());
          const hide = !overflows();
          prev.classList.toggle('hidden', hide);
          next.classList.toggle('hidden', hide);
        }
        if (dots.length) {
          const pageW = step() * cols() || track.clientWidth || 1;
          const active = Math.min(dots.length - 1, Math.round(track.scrollLeft / pageW));
          dots.forEach((d, i) => d.classList.toggle('is-active', i === active));
        }
      };

      let raf = false;
      track.addEventListener('scroll', () => {
        if (raf) return;
        raf = true;
        requestAnimationFrame(() => { sync(); raf = false; });
      }, { passive: true });

      window.addEventListener('resize', () => { buildDots(); sync(); }, { passive: true });

      buildDots();
      sync();

      if (autoplay) {
        let timer = null;
        const stop = () => { clearInterval(timer); timer = null; };
        const start = () => { if (!timer && overflows()) timer = setInterval(() => go(1), interval); };
        slider.addEventListener('mouseenter', stop);
        slider.addEventListener('mouseleave', start);
        track.addEventListener('pointerdown', stop, { passive: true });
        start();
      }
    }
  }

  #initAutoScroll() {
    document.querySelectorAll('.grid-cards__blocks.slider[data-autoscroll="true"]').forEach(track => {
      const colsMobile = parseInt(track.dataset.sliderColumnsMb, 10) || 1;
      const colsTablet = parseInt(track.dataset.sliderColumnsTb, 10) || colsMobile;
      const colsDesktop = parseInt(track.dataset.sliderColumns, 10) || colsTablet;
      const desktopEnabled = track.hasAttribute('data-slider-desktop');
      const interval = parseInt(track.dataset.autoscrollInterval, 10) || 3000;
      let current = 0, auto = null, hovered = false;

      const getCols = () => {
        if (window.innerWidth >= 1024) return desktopEnabled ? colsDesktop : null;
        return window.innerWidth >= 540 ? colsTablet : colsMobile;
      };

      const cards = () => [...track.querySelectorAll('.grid-cards__block')];
      const stop = () => { clearInterval(auto); auto = null; };
      const start = () => {
        if (!auto && !hovered && getCols() !== null) auto = setInterval(next, interval);
      };

      const next = () => {
        const cols = getCols();
        if (cols === null) return stop();
        const items = cards();
        if (!items.length) return;
        current = (current + cols) % items.length;
        track.scrollTo({ left: items[current].offsetLeft - track.offsetLeft, behavior: 'smooth' });
      };

      track.addEventListener('mouseenter', () => { hovered = true; stop(); });
      track.addEventListener('mouseleave', () => { hovered = false; start(); });
      track.addEventListener('scrollend', () => {
        const items = cards();
        const i = items.findIndex(c => c.offsetLeft - track.offsetLeft >= track.scrollLeft - 1);
        if (i !== -1) current = i;
        stop(); start();
      }, { passive: true });

      window.addEventListener('resize', () => { stop(); start(); }, { passive: true });

      start();
    });
  }
}

document.addEventListener('DOMContentLoaded', function () {
  const reusableComponents = new ReusableComponents();
  reusableComponents.init();
});